import React, { useState, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useServices } from "@/services-context";
import accountIcon from "@/assets/account.svg";
import carIcon from "@/assets/car.svg";
import { features } from "@/constants/features.ts";
import styles from "./MainScreen.module.scss";
import { LayoutSmContainer } from "@/elements-react/menu/LayoutSm";
import { DialogMobile } from "@/elements-react/dialog-mobile";
import CustomerActions from "@/elements-react/customer-actions";
import { ItemButton } from "@/elements-react/menu/ItemButton";
import { FixDropdownInputCountry, FixDropdownInputCurrency, FixDropdownInputLanguage } from "@/elements-react/fix-dropdown-connected";
import { useLayoutFragment } from "@/elements-react/menu/LayoutFragment.tsx";
import { LayoutStyleTypes } from "@/util-react/layout.tsx";

type MenuItem = {
  id: string;
  name: string;
  icon?: string;
};

type MainScreenProps = {
  items: MenuItem[];
  onToCategory?: (key: string) => void;
};

const MainScreen: React.FC<MainScreenProps> = ({ items, onToCategory }) => {
  const { t } = useTranslation();
  const [dialogAccount, setDialogAccount] = useState(false);

  const services = useServices();
  const customerChannel = services.customerChannel;
  const logged = true;
  const label = customerChannel?.isCustomerAuth() ? customerChannel.customer?.email : t("7e19h22khwn365zm.sign-in", "Sign in");

  const renderAccount = useMemo(
    () =>
      useLayoutFragment((layout) =>
        layout.renderSection({
          segment: "account",
          content: () => (
            <div className={styles.items}>
              {dialogAccount && (
                <DialogMobile label={t("1ystg2f5ui8t2310.my-account", "My account")} onClose={() => setDialogAccount(false)}>
                  <CustomerActions type={LayoutStyleTypes.TYPE_SM} />
                </DialogMobile>
              )}
              <ItemButton
                title={t("1ystg2f5ui8t2310.my-account", "My account")}
                sub={label}
                schema="column"
                icon={accountIcon}
                onClick={() => setDialogAccount(true)}
              />
              {features.garage && <ItemButton title="My garage" sub="2015 Ford Focus" count={1} schema="column" icon={carIcon} />}
            </div>
          ),
        }),
      ),
    [dialogAccount, label, t],
  );

  const renderCategories = useMemo(
    () =>
      useLayoutFragment((layout) =>
        layout.renderSection({
          segment: "categories",
          title: t("83ao1kah0uewlr1m.categories", "Categories"),
          content: () => (
            <div className={styles.items}>
              {items.map((item) => (
                <ItemButton key={item.id} title={item.name} icon={item.icon} onClick={() => onToCategory?.(item.id)} />
              ))}
            </div>
          ),
        }),
      ),
    [items, onToCategory, t],
  );

  const renderSettings = useMemo(
    () =>
      useLayoutFragment((layout) =>
        layout.renderSection({
          segment: "settings",
          title: t("4lefxn4fnao06t1b.settings", "Settings"),
          content: () => (
            <div className={styles.items}>
              <FixDropdownInputCountry type={LayoutStyleTypes.TYPE_SM} />
              <FixDropdownInputCurrency type={LayoutStyleTypes.TYPE_SM} />
              <FixDropdownInputLanguage type={LayoutStyleTypes.TYPE_SM} />
            </div>
          ),
        }),
      ),
    [t],
  );

  const renderHeadMain = useLayoutFragment((layout) => layout.renderHeadMain());
  const renderDelimiter = useLayoutFragment((layout) => layout.renderDelimiter());

  return (
    <div className={classNames(styles.root)}>
      {renderHeadMain}
      {logged && renderAccount}
      {renderDelimiter}
      {renderCategories}
      {renderDelimiter}
      {renderSettings}
    </div>
  );
};

export default MainScreen;
